import { signal } from "@angular/core";
import type { GridApi, IRowNode } from "ag-grid-community";

/**
 * Past list intro (#5637) - GLOSS list-motion prototype, with the "month by month" feel:
 *   pending  the list builds underneath the skeleton, invisible
 *   flat     it fades in as one piece (no month headers) and holds for a moment
 *   run      each month in turn: its header grows while its rows collapse, on a stagger
 * It ends in the grid's normal resting state (every month closed), so the intro only ever animates
 * towards what a plain render would show.
 *
 * The list is a plain ag-Grid (autoHeight + row grouping), so there is no wrapper element to animate. The intro
 * steers it through public API:
 *  - pending/flat  months render expanded, their group rows forced to 1px (ag-Grid clamps heights to >= 1), the
 *                  header components hidden (`isHeaderPending`), the grid host faded out until it is ready
 *  - run           every month has its own clock, so heights are written from script each frame
 *                  (`rowNode.setRowHeight` + one `api.onRowHeightChanged()`); CSS transitions on the rows are
 *                  switched off (`.past-intro-run`) so nothing fights the script. A month's header fade, chevron
 *                  turn and row fade are CSS transitions started once, at the month's own start, by refreshing
 *                  that month's cells. As a month's rows finish collapsing (1px each) they are dropped and the
 *                  px they still took are handed to the header, which settles them with a short ease: no pop.
 * (ag-Grid removing a month's rows itself would fade them in place while the rows below slide over them, hence
 * the intro collapses them first.)
 *
 * Keeping it smooth: ag-Grid instantiates cell renderers a frame at a time (about 60ms of work per frame), and
 * these renderers are Angular components, so every row rendered while flat costs real frame time. autoHeight turns
 * row virtualisation off, so the flat phase renders only the first rows that fit the viewport (an external filter
 * hides the rest; every month keeps one row so its header still exists). The clock only starts when the list is
 * actually built, which is read from the grid (no row data change for a moment, every renderer instantiated),
 * not guessed from a fixed delay. Row data pushed after that point makes ag-Grid rebuild every row again: during
 * the hold the intro waits for the list to be built once more, and mid-months it stops and snaps to the resting
 * state (the nodes it was steering are gone).
 */

type PastIntroPhase = "pending" | "flat" | "run";

type PastIntroEasing = (t: number) => number;

export interface PastIntroTiming {
  /** How long the revealed flat list holds before the first month starts. */
  holdMs: number;
  /** One month's header growing. */
  headerMs: number;
  /** One month's rows collapsing. */
  collapseMs: number;
  /** Delay between one month starting and the next. */
  staggerMs: number;
}

interface PastIntroEasings {
  header: PastIntroEasing;
  collapse: PastIntroEasing;
}

interface PastIntroRowHeights {
  /** A month header row. */
  group: number;
  /** A transaction row. */
  data: number;
}

const FALLBACK_TIMING: PastIntroTiming = {
  holdMs: 170,
  headerMs: 340,
  collapseMs: 800,
  staggerMs: 160,
};

type BezierPoints = [number, number, number, number];
const FALLBACK_HEADER_EASE: BezierPoints = [0.23, 1, 0.32, 1];
const FALLBACK_COLLAPSE_EASE: BezierPoints = [0.25, 0.8, 0.25, 1];

/** ag-Grid clamps row heights to >= 1 and warns on 0, so "hidden" rows are 1px tall. */
const FORCED_ROW_PX = 1;

/** Any of these during the hold means the user is already interacting: start the months right away. */
const HOLD_ENDING_EVENTS = ["wheel", "touchstart", "pointerdown", "keydown"] as const;

/** Rows rendered below the last one that fits the viewport, so the fold never shows a gap. */
const FLAT_BUFFER_ROWS = 2;

/** The longest the cascade of closed months below the fold runs on for, however many there are. */
const REST_SPREAD_MS = 600;

/**
 * "The list is built" means the row data has been quiet for DATA_QUIET_MS (the stores update it a couple of
 * times right after the grid appears), every cell renderer has been instantiated, and that has held for
 * READY_FRAMES frames so it has painted.
 */
const DATA_QUIET_MS = 120;
const READY_FRAMES = 2;
/** Don't hold the intro back for more than this if the grid never reports ready (a hidden tab, say). */
const READY_FALLBACK_MS = 2500;

export function parseCssDurationMs(raw: string | null | undefined, fallback: number): number {
  const value = (raw ?? "").trim();
  const ms = value.endsWith("ms")
    ? parseFloat(value)
    : value.endsWith("s")
      ? parseFloat(value) * 1000
      : NaN;
  return Number.isFinite(ms) ? ms : fallback;
}

/** An easing function for the control points of a CSS cubic-bezier(). */
export function cubicBezier(x1: number, y1: number, x2: number, y2: number): PastIntroEasing {
  return (t: number) => {
    if (t <= 0) {
      return 0;
    }
    if (t >= 1) {
      return 1;
    }
    let lo = 0;
    let hi = 1;
    let u = t;
    for (let i = 0; i < 24; i++) {
      const x = 3 * (1 - u) * (1 - u) * u * x1 + 3 * (1 - u) * u * u * x2 + u * u * u;
      if (x < t) {
        lo = u;
      } else {
        hi = u;
      }
      u = (lo + hi) / 2;
    }
    return 3 * (1 - u) * (1 - u) * u * y1 + 3 * (1 - u) * u * u * y2 + u * u * u;
  };
}

/** Reads `cubic-bezier(a, b, c, d)` from a CSS token value, falling back to the given points. */
export function parseCubicBezier(
  raw: string | null | undefined,
  fallback: BezierPoints,
): PastIntroEasing {
  const match =
    /cubic-bezier\(\s*(-?[\d.]+)\s*,\s*(-?[\d.]+)\s*,\s*(-?[\d.]+)\s*,\s*(-?[\d.]+)\s*\)/.exec(
      raw ?? "",
    );
  const points = match ? (match.slice(1, 5).map(Number) as BezierPoints) : fallback;
  return cubicBezier(...(points.every(Number.isFinite) ? points : fallback));
}

type StyleSource = Pick<CSSStyleDeclaration, "getPropertyValue">;

const defaultStyle = (): StyleSource => getComputedStyle(document.documentElement);

/**
 * Durations and curves come from the CSS tokens (the unratified --x-emil-past-group-* ones in material-ui.scss,
 * and --ease-out) so design can retune them in one place; the literals are only the fallback when a token
 * isn't defined.
 */
export function readPastIntroTiming(style: StyleSource = defaultStyle()): PastIntroTiming {
  // The optional call is for the repo's jest setup (tests/test.setup.ts), which stubs getComputedStyle with an
  // object that has no getPropertyValue; real browsers always have it.
  const read = (token: string, fallback: number) =>
    parseCssDurationMs(style.getPropertyValue?.(token), fallback);
  return {
    holdMs: read("--x-emil-past-group-hold", FALLBACK_TIMING.holdMs),
    headerMs: read("--x-emil-past-group-header", FALLBACK_TIMING.headerMs),
    collapseMs: read("--x-emil-past-group-collapse", FALLBACK_TIMING.collapseMs),
    staggerMs: read("--x-emil-past-group-stagger", FALLBACK_TIMING.staggerMs),
  };
}

export function readPastIntroEasings(style: StyleSource = defaultStyle()): PastIntroEasings {
  return {
    header: parseCubicBezier(style.getPropertyValue?.("--ease-out"), FALLBACK_HEADER_EASE),
    collapse: parseCubicBezier(
      style.getPropertyValue?.("--x-emil-past-group-ease-collapse"),
      FALLBACK_COLLAPSE_EASE,
    ),
  };
}

/**
 * 0..1 for how far `t` is through a window of `ms` that began at `start`. A window with no length (a 0ms token)
 * is done the moment it starts.
 */
export function windowProgress(t: number, start: number, ms: number): number {
  if (ms <= 0) {
    return t >= start ? 1 : 0;
  }
  return Math.min(1, Math.max(0, (t - start) / ms));
}

const lerp = (from: number, to: number, p: number) => from + (to - from) * p;

interface FlatPhasePlan {
  /** Groups that render expanded while flat: the ones holding the first `visibleRows` rows. */
  openKeys: ReadonlySet<string>;
  /** How many rows (in list order) render while flat. */
  visibleRows: number;
}

/**
 * What the flat list renders: just the first rows that fit the viewport (plus a small buffer), and the
 * groups they belong to. `dates` is in list order, which is also the order the grid builds its groups in.
 */
export function planFlatPhase(
  dates: ReadonlyArray<string | null | undefined>,
  keyOf: (date: string) => string,
  viewportPx: number,
  rowPx: number,
): FlatPhasePlan {
  const visibleRows = Math.ceil(viewportPx / rowPx) + FLAT_BUFFER_ROWS;
  const openKeys = new Set<string>();
  for (const date of dates.slice(0, visibleRows)) {
    const key = date ? keyOf(date) : "";
    if (key) {
      openKeys.add(key);
    }
  }
  return { openKeys, visibleRows };
}

/**
 * True once ag-Grid has instantiated every cell renderer for the rows it is showing: the group header
 * renderers plus one per renderer column for each row. It builds those a frame at a time, so before this
 * is true the list is still filling in.
 */
export function renderersBuilt(api: GridApi): boolean {
  const nodes = api.getRenderedNodes();
  const rendererColumns = api
    .getAllDisplayedColumns()
    .filter((column) => !!column.getColDef().cellRenderer).length;
  const groups = nodes.filter((node) => node.group).length;
  const rows = nodes.filter((node) => !node.group && !node.detail).length;
  return api.getCellRendererInstances().length >= groups + rows * rendererColumns;
}

/**
 * Resolves once the list is built: no row data change for DATA_QUIET_MS, every renderer instantiated, and
 * that has held for READY_FRAMES frames (so it has painted). Also resolves if `isCancelled()` turns true or the
 * grid is destroyed; the caller owns the overall timeout.
 */
export function whenGridReady(
  api: GridApi,
  lastDataChange: () => number,
  isCancelled: () => boolean,
): Promise<void> {
  return new Promise((resolve) => {
    let steady = 0;
    const onFrame = () => {
      if (isCancelled() || api.isDestroyed()) {
        resolve();
        return;
      }
      const quiet = performance.now() - lastDataChange() >= DATA_QUIET_MS;
      steady = quiet && renderersBuilt(api) ? steady + 1 : 0;
      if (steady >= READY_FRAMES) {
        resolve();
      } else {
        requestAnimationFrame(onFrame);
      }
    };
    requestAnimationFrame(onFrame);
  });
}

export interface PastGroupIntroOptions {
  /** What the flat phase renders. Resolved lazily; re-resolved after each row data change. */
  plan: () => FlatPhasePlan;
  rowHeights: PastIntroRowHeights;
  /** Overrides the timing read from the CSS tokens; a test seam. */
  timing?: PastIntroTiming;
  /** Overrides the easings read from the CSS tokens; a test seam. */
  easings?: PastIntroEasings;
  /** Wraps signal writes so change detection sees them (the grid calls back outside Angular's zone). */
  run?: (fn: () => void) => void;
  onFinish?: () => void;
  /** Resolves once the list is built. Defaults to reading it from the grid; a test seam. */
  whenReady?: (isCancelled: () => boolean) => Promise<void>;
}

interface RunningMonth {
  node: IRowNode;
  start: number;
  leaves: IRowNode[];
  /** When the rows finished collapsing and were dropped; null until then. */
  removedAt: number | null;
}

const displayedChildren = (node: IRowNode): IRowNode[] =>
  node.childrenAfterFilter ?? node.childrenAfterGroup ?? [];

export class PastGroupIntro {
  /** Drives the phase class on the grid host (see PastTransactionTable.motionClass). */
  readonly phase = signal<PastIntroPhase>("pending");

  private readonly timing: PastIntroTiming;
  private readonly easings: PastIntroEasings;
  private readonly run: (fn: () => void) => void;
  private flatPlan: FlatPhasePlan | null = null;
  private filtering = true;
  private finished = false;
  private lastDataChange = performance.now();
  private dataChanges = 0;
  private readonly collapsing = new Set<string>();
  private readonly revealed = new Set<string>();
  private stopHold: (() => void) | null = null;
  private cancelled = false;
  private started = false;

  constructor(private readonly options: PastGroupIntroOptions) {
    this.timing = options.timing ?? readPastIntroTiming();
    this.easings = options.easings ?? readPastIntroEasings();
    this.run = options.run ?? ((fn) => fn());
  }

  private get plan(): FlatPhasePlan {
    this.flatPlan ??= this.options.plan();
    return this.flatPlan;
  }

  /**
   * The grid's row data was (re)set. The stores do that a couple of times right after the list appears, and
   * ag-Grid rebuilds the rows each time: the list isn't built until this has gone quiet, and months the intro
   * is already steering are no longer the grid's nodes.
   */
  noteDataChange(): void {
    this.lastDataChange = performance.now();
    this.dataChanges++;
    this.flatPlan = null; // derived from the data
  }

  isOpenByDefault(key: string | null | undefined): boolean {
    return key != null && this.plan.openKeys.has(key);
  }

  /** True once `start()` has been called (and not before). */
  get hasStarted(): boolean {
    return this.started;
  }

  /** `isExternalFilterPresent` hook: true while the intro is limiting how many rows render. */
  get isFiltering(): boolean {
    return this.filtering;
  }

  /**
   * `doesExternalFilterPass` hook. While the intro runs, a row renders if it is among the first rows that fit
   * the viewport, or if it is the first row of its month: a month with no passing row would have no header to
   * bring in. Those lone rows sit in collapsed months below the fold, so they never show.
   */
  passes(node: IRowNode): boolean {
    if (!this.filtering || node.group) {
      return true;
    }
    return (
      node.sourceRowIndex < this.plan.visibleRows || node.parent?.allLeafChildren?.[0] === node
    );
  }

  /** True while a month's rows are collapsing (its header turns closed before the grid collapses the group). */
  isCollapsing(key: string | null | undefined): boolean {
    return key != null && this.collapsing.has(key);
  }

  /** True while this month's header should be hidden: until the month's own turn comes up. */
  isHeaderPending(key: string | null | undefined): boolean {
    return !this.finished && (this.phase() !== "run" || key == null || !this.revealed.has(key));
  }

  /** `getRowHeight` hook: a forced height for rows the intro is hiding, undefined to defer to the table. */
  rowHeight(node: IRowNode): number | undefined {
    if (node.group) {
      return this.phase() === "run" ? undefined : FORCED_ROW_PX;
    }
    return this.isCollapsing(node.parent?.key) ? FORCED_ROW_PX : undefined;
  }

  /**
   * Stops the intro: an unfinished hold ends, the months stop being written, start() settles without finish()
   * (so no onFinish), and a later start() does nothing. It does not restore the grid: group rows keep their
   * forced/animated heights, collapsing leaves stay at 1px, and ag-Grid keeps its cached
   * `isExternalFilterPresent` (which this intro still answers true to) until `onFilterChanged()`. Callers must
   * destroy the grid, or rebuild its rows (`rowData`) and call `api.onFilterChanged()`.
   */
  cancel(): void {
    this.cancelled = true;
    this.stopHold?.();
  }

  /**
   * Runs the choreography. Resolves when it finishes or is cancelled. A step that throws ends it in the resting
   * state instead of rejecting; it rejects only if finishing itself throws.
   */
  async start(api: GridApi): Promise<void> {
    if (this.started || this.cancelled) {
      return;
    }
    this.started = true;
    let interrupted = false;
    try {
      interrupted = !(await this.play(api));
    } catch {
      // Whatever threw, the list must not stay hidden under the skeleton with its rows squashed to 1px: end in
      // the resting state, the way new row data cutting the months short does. The error is dropped on purpose:
      // a failed intro degrades to the plain list rather than breaking the page.
      interrupted = true;
    }
    if (this.alive(api)) {
      this.finish(api, interrupted);
    }
  }

  /** Resolves true when every month played out, false when it was cut short (or cancelled, which skips finish). */
  private async play(api: GridApi): Promise<boolean> {
    await this.untilReady(api);
    if (!this.alive(api)) {
      return false;
    }

    this.setPhase("flat");
    const dataChangesAtReveal = this.dataChanges;
    const byUser = await this.holdOrInput(this.timing.holdMs);
    if (!this.alive(api)) {
      return false;
    }

    // Row data pushed while the list was up makes ag-Grid rebuild its rows, which is what the months would
    // otherwise start on top of: let it get built again first (unless the user is already reaching for the list).
    if (!byUser && this.dataChanges !== dataChangesAtReveal) {
      await this.untilReady(api);
      if (!this.alive(api)) {
        return false;
      }
    }

    this.setPhase("run");
    return this.runMonths(api);
  }

  /** The list is built (read from the grid), or the fallback timeout ran out. */
  private untilReady(api: GridApi): Promise<void> {
    let settled = false;
    const isCancelled = () => this.cancelled || settled;
    const waiting = this.options.whenReady
      ? this.options.whenReady(isCancelled)
      : whenGridReady(api, () => this.lastDataChange, isCancelled);
    let fallback: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<void>((resolve) => {
      fallback = setTimeout(resolve, READY_FALLBACK_MS);
    });
    return Promise.race([waiting, timeout]).then(() => {
      settled = true;
      clearTimeout(fallback);
    });
  }

  /**
   * Every month at once but each on its own clock: months start `staggerMs` apart, and a month's header
   * grows while its rows collapse. The months below the fold (still closed) bring their headers in as the cascade's
   * tail.
   * The clock starts on the first frame, so a slow first frame doesn't eat into the stagger. Resolves true when
   * every month has played, false if it was cancelled (or the grid destroyed) or new row data replaced the nodes
   * under it, and rejects if a frame throws.
   */
  private runMonths(api: GridApi): Promise<boolean> {
    const { headerMs, collapseMs, staggerMs } = this.timing;
    const open: RunningMonth[] = [];
    const rest: IRowNode[] = [];
    api.forEachNode((node) => {
      if (!node.group) {
        return;
      }
      if (node.expanded) {
        open.push({
          node,
          start: open.length * staggerMs,
          leaves: displayedChildren(node),
          removedAt: null,
        });
      } else {
        rest.push(node);
      }
    });
    // The months below the fold carry the cascade on after the open ones. A long history would drag it out, so
    // their stagger shrinks to keep that tail within REST_SPREAD_MS.
    const restStart = open.length * staggerMs;
    const restStagger = rest.length ? Math.min(staggerMs, REST_SPREAD_MS / rest.length) : 0;
    const total = Math.max(
      open.length ? (open.length - 1) * staggerMs + collapseMs + headerMs : 0,
      rest.length ? restStart + (rest.length - 1) * restStagger + headerMs : 0,
    );

    const dataChangesAtStart = this.dataChanges;
    return new Promise((resolve, reject) => {
      let t0: number | null = null;
      const frame = () => {
        if (!this.alive(api) || this.dataChanges !== dataChangesAtStart) {
          resolve(false);
          return;
        }
        const now = performance.now();
        t0 ??= now;
        const t = now - t0;
        try {
          open.forEach((month) => this.tickMonth(api, month, t));
          rest.forEach((node, i) => this.tickRest(api, node, restStart + i * restStagger, t));
          api.onRowHeightChanged();
        } catch (error) {
          // A throw out of a frame callback would never reach start(): hand it over, or the run never ends.
          reject(error);
          return;
        }
        if (t >= total) {
          resolve(true);
        } else {
          requestAnimationFrame(frame);
        }
      };
      requestAnimationFrame(frame);
    });
  }

  private tickMonth(api: GridApi, month: RunningMonth, t: number): void {
    const { headerMs, collapseMs } = this.timing;
    const { group, data } = this.options.rowHeights;
    const { node, start } = month;

    if (t >= start && node.key != null && !this.revealed.has(node.key)) {
      this.revealed.add(node.key);
      this.collapsing.add(node.key);
      // One refresh each: the header fades in and its chevron turns (CSS transitions), and the rows fade.
      api.refreshCells({ rowNodes: [node], force: true });
      api.refreshCells({ rowNodes: month.leaves });
    }

    const headerP = this.easings.header(windowProgress(t, start, headerMs));
    const collapseP = this.easings.collapse(windowProgress(t, start, collapseMs));

    if (month.removedAt === null) {
      month.leaves.forEach((leaf) => leaf.setRowHeight(lerp(data, FORCED_ROW_PX, collapseP)));
      if (collapseP >= 1) {
        // The rows are flat: drop them, and let the header settle the px they still took.
        month.removedAt = t;
        node.setExpanded(false);
      }
    }

    // Dropping the flattened rows takes their px (1px each) out of the layout in one go, so the header takes them
    // over in the same frame (everything below the month stays exactly where it is) and then lets them go with a
    // short ease. Spreading them onto the header while the rows still exist would count them twice and make every
    // row below jump up by that much at the drop.
    const settle =
      month.removedAt === null
        ? 0
        : month.leaves.length *
          FORCED_ROW_PX *
          (1 - this.easings.header(windowProgress(t, month.removedAt, headerMs)));
    node.setRowHeight(lerp(FORCED_ROW_PX, group, headerP) + settle);
  }

  private tickRest(api: GridApi, node: IRowNode, start: number, t: number): void {
    if (t >= start && node.key != null && !this.revealed.has(node.key)) {
      this.revealed.add(node.key);
      api.refreshCells({ rowNodes: [node], force: true });
    }
    const p = this.easings.header(windowProgress(t, start, this.timing.headerMs));
    node.setRowHeight(lerp(FORCED_ROW_PX, this.options.rowHeights.group, p));
  }

  /**
   * `interrupted`: the months didn't play out (new row data cut them short, or a step threw), so the grid
   * has reopened the plan's months.
   */
  private finish(api: GridApi, interrupted: boolean): void {
    this.finished = true;
    this.collapsing.clear();
    if (interrupted) {
      // Put it in the resting state the months were heading for.
      api.collapseAll();
    }
    // ag-Grid caches each row's height on its node, so the rows of the months that just collapsed would keep
    // the 1px they were shrunk to, and tapping a header later would open an empty strip. Marking every node's
    // height stale makes it re-measure (now at the real height) the next time a row is shown.
    api.resetRowHeights();
    // Every month is closed at this point, so lifting the row limit only restores rows that stay hidden.
    this.filtering = false;
    api.onFilterChanged();
    this.options.onFinish?.();
  }

  private setPhase(phase: PastIntroPhase): void {
    this.run(() => this.phase.set(phase));
  }

  private alive(api: GridApi): boolean {
    return !this.cancelled && !api.isDestroyed();
  }

  /** The revealed list holds for `ms`; user input ends the wait early (resolves true then). */
  private holdOrInput(ms: number): Promise<boolean> {
    return new Promise((resolve) => {
      const end = (byUser: boolean) => {
        clearTimeout(timer);
        HOLD_ENDING_EVENTS.forEach((type) =>
          document.removeEventListener(type, onInput, { capture: true }),
        );
        this.stopHold = null;
        resolve(byUser);
      };
      const onInput = () => end(true);
      const timer = setTimeout(() => end(false), ms);
      HOLD_ENDING_EVENTS.forEach((type) =>
        document.addEventListener(type, onInput, { capture: true, passive: true }),
      );
      this.stopHold = () => end(false);
    });
  }
}
